/**
 * Support & Tickets Management Page
 * ==================================
 * صفحة الدعم الفني والتذاكر
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import '../shared/PageStyles.css';

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  ticket: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
};

const priorityMap = { low: { label: 'منخفضة', color: 'info' }, medium: { label: 'متوسطة', color: 'warning' }, high: { label: 'عالية', color: 'danger' } };
const statusMap = { open: { label: 'مفتوحة', color: 'warning' }, in_progress: { label: 'قيد المعالجة', color: 'primary' }, resolved: { label: 'تم الحل', color: 'success' }, closed: { label: 'مغلقة', color: 'info' } };

const SupportPage = () => {
  const { apiCall, user } = useTenantAuth();
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [formData, setFormData] = useState({ subject: '', message: '', priority: 'medium', category: 'general' });

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const data = await apiCall(`/tenant/support/tickets?${params}`);
      setTickets(data.items || []);
    } catch (err) {
      showError('فشل تحميل التذاكر');
    } finally {
      setLoading(false);
    }
  }, [apiCall, search, statusFilter, showError]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiCall('/tenant/support/tickets', { method: 'POST', body: JSON.stringify(formData) });
      success('تم إنشاء التذكرة بنجاح');
      setShowModal(false);
      setFormData({ subject: '', message: '', priority: 'medium', category: 'general' });
      loadTickets();
    } catch (err) {
      showError(err.message || 'فشل إنشاء التذكرة');
    }
  };

  const viewTicket = async (ticketId) => {
    try {
      const data = await apiCall(`/tenant/support/tickets/${ticketId}`);
      setSelectedTicket(data);
      setShowDetails(true);
    } catch (err) {
      showError('فشل تحميل تفاصيل التذكرة');
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    try {
      await apiCall(`/tenant/support/tickets/${selectedTicket.id}/reply`, {
        method: 'POST',
        body: JSON.stringify({ message: replyText })
      });
      success('تم إرسال الرد');
      setReplyText('');
      viewTicket(selectedTicket.id);
    } catch (err) {
      showError('فشل إرسال الرد');
    }
  };

  const resolveTicket = async () => {
    try {
      await apiCall(`/tenant/support/tickets/${selectedTicket.id}/resolve`, { method: 'PUT' });
      success('تم إغلاق التذكرة');
      setShowDetails(false);
      loadTickets();
    } catch (err) {
      showError('فشل إغلاق التذكرة');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('ar-SA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__title">
          <h1>الدعم الفني</h1>
          <p>{tickets.length} تذكرة</p>
        </div>
        <button className="btn btn--primary" onClick={() => setShowModal(true)}>
          {I.plus} <span>تذكرة جديدة</span>
        </button>
      </div>

      <div className="page-filters">
        <div className="search-box">
          {I.search}
          <input type="text" placeholder="بحث بالموضوع..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
          <option value="all">جميع الحالات</option>
          {Object.entries(statusMap).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : tickets.length === 0 ? (
        <div className="page-empty">{I.ticket}<h3>لا يوجد تذاكر</h3><p>لم يتم العثور على تذاكر</p></div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>رقم التذكرة</th>
                <th>الموضوع</th>
                <th>الأولوية</th>
                <th>الحالة</th>
                <th>التاريخ</th>
                <th>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map(t => (
                <tr key={t.id}>
                  <td style={{ fontWeight: '600' }}>#{t.ticket_number}</td>
                  <td>{t.subject}</td>
                  <td><span className={`badge badge--${priorityMap[t.priority]?.color}`}>{priorityMap[t.priority]?.label}</span></td>
                  <td><span className={`badge badge--${statusMap[t.status]?.color}`}>{statusMap[t.status]?.label}</span></td>
                  <td style={{ fontSize: '12px', color: 'var(--tenant-text-muted)' }}>{formatDate(t.created_at)}</td>
                  <td>
                    <div className="actions">
                      <button className="action-btn" onClick={() => viewTicket(t.id)}>{I.eye}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Ticket Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>تذكرة دعم جديدة</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>{I.x}</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group full-width"><label>الموضوع *</label><input type="text" value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} required /></div>
                  <div className="form-group">
                    <label>الأولوية</label>
                    <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})}>
                      <option value="low">منخفضة</option>
                      <option value="medium">متوسطة</option>
                      <option value="high">عالية</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>التصنيف</label>
                    <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})}>
                      <option value="general">عام</option>
                      <option value="technical">تقني</option>
                      <option value="billing">المحاسبة</option>
                      <option value="orders">الطلبات</option>
                    </select>
                  </div>
                  <div className="form-group full-width"><label>الرسالة *</label><textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required /></div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn--secondary" onClick={() => setShowModal(false)}>إلغاء</button>
                <button type="submit" className="btn btn--primary">إرسال</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ticket Details Modal */}
      {showDetails && selectedTicket && (
        <div className="modal-overlay" onClick={() => setShowDetails(false)}>
          <div className="modal" style={{ maxWidth: '700px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>{selectedTicket.subject}</h2>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <span className={`badge badge--${priorityMap[selectedTicket.priority]?.color}`}>{priorityMap[selectedTicket.priority]?.label}</span>
                  <span className={`badge badge--${statusMap[selectedTicket.status]?.color}`}>{statusMap[selectedTicket.status]?.label}</span>
                </div>
              </div>
              <button className="modal-close" onClick={() => setShowDetails(false)}>{I.x}</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {/* Messages */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {selectedTicket.comments?.map((c, i) => (
                  <div key={i} style={{ padding: '12px 16px', background: c.is_admin ? 'rgba(14, 165, 233, 0.1)' : 'var(--tenant-bg)', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <strong style={{ fontSize: '13px' }}>{c.author_name || (c.is_admin ? 'الدعم الفني' : user?.name)}</strong>
                      <span style={{ fontSize: '11px', color: 'var(--tenant-text-muted)' }}>{formatDate(c.created_at)}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.6' }}>{c.message}</p>
                  </div>
                ))}
              </div>
            </div>
            {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
              <div style={{ padding: '16px 24px', borderTop: '1px solid var(--tenant-border)' }}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <input
                    type="text"
                    placeholder="اكتب ردك..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    style={{ flex: 1, padding: '10px 14px', border: '1px solid var(--tenant-border)', borderRadius: '8px' }}
                  />
                  <button className="btn btn--primary" onClick={sendReply} style={{ padding: '10px 16px' }}>{I.send}</button>
                </div>
              </div>
            )}
            <div className="modal-footer">
              {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
                <button className="btn btn--secondary" onClick={resolveTicket}>{I.check} <span>تم الحل</span></button>
              )}
              <button className="btn btn--secondary" onClick={() => setShowDetails(false)}>إغلاق</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportPage;
